import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent,
  CircularProgress,
  Tooltip,
  Badge,
} from '@mui/material';
import { 
  Whatshot as FireIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import languageLearningApi from '../middleware/languageLearningApi';

const UserStreakComponent = ({ userId, courseId, cardStyle }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [streakData, setStreakData] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [daysCompletedThisMonth, setDaysCompletedThisMonth] = useState(0);
  
  // Generate calendar data by weekday
  const generateCalendarData = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Create rows for each weekday (0 = Monday to 6 = Sunday)
    const weekdayData = [
      { weekday: 'M', days: [] }, // Shortened weekday labels
      { weekday: 'T', days: [] },
      { weekday: 'W', days: [] },
      { weekday: 'T', days: [] },
      { weekday: 'F', days: [] },
      { weekday: 'S', days: [] },
      { weekday: 'S', days: [] }
    ];
    
    // Get number of weeks in the month to determine columns
    let maxWeeks = 0;
    
    // Fill in days of the month by weekday
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
      const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to 0 = Monday, 6 = Sunday
      
      // Calculate week number for this day (0-indexed)
      const firstDayOfMonth = new Date(year, month, 1);
      const firstDayOfWeek = firstDayOfMonth.getDay() === 0 ? 6 : firstDayOfMonth.getDay() - 1;
      const weekNum = Math.floor((day + firstDayOfWeek - 1) / 7);
      
      if (weekNum + 1 > maxWeeks) {
        maxWeeks = weekNum + 1;
      }
      
      // Add day to appropriate weekday row
      weekdayData[adjustedDayOfWeek].days.push({
        day,
        date,
        week: weekNum,
        dateString: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      });
    }
    
    // Fill empty weeks for days that don't have entries in all weeks
    weekdayData.forEach(weekday => {
      const filledWeeks = new Set(weekday.days.map(d => d.week));
      
      // Create a map of weeks to days
      const weekToDayMap = {};
      weekday.days.forEach(day => {
        weekToDayMap[day.week] = day;
      });
      
      // Create a new array with placeholders for missing weeks
      const filledDays = [];
      for (let week = 0; week < maxWeeks; week++) {
        filledDays.push(weekToDayMap[week] || null);
      }
      
      weekday.days = filledDays;
    });
    
    return { weekdayData, weekCount: maxWeeks };
  };
  
  // Function to check if a date has a completed goal - FIXED to handle UTC dates correctly
  const hasCompletedGoal = (dateString) => {
    if (!streakData || !streakData.datesWithGoalCompleted) return false;
    
    return streakData.datesWithGoalCompleted.some(goalDate => {
      // Convert the UTC date to match our local date comparison format
      // Use UTC methods to get the date parts to avoid timezone shifts
      const d = new Date(goalDate);
      
      // Create date string in YYYY-MM-DD format using UTC date parts
      const goalDateString = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
      
      return goalDateString === dateString;
    });
  };
  
  // Function to calculate current streak
  const calculateCurrentStreak = () => {
    if (!streakData || !streakData.datesWithGoalCompleted || streakData.datesWithGoalCompleted.length === 0) {
      return 0;
    }
    
    // Sort dates in ascending order, using UTC to avoid timezone issues
    const sortedDates = [...streakData.datesWithGoalCompleted]
      .map(d => {
        // Create date using UTC to avoid timezone shifts
        const date = new Date(d);
        return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
      })
      .sort((a, b) => a - b);
    
    const today = new Date();
    // Create UTC today date for comparison
    const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    
    // Check if the latest date is today or yesterday
    const latestDate = sortedDates[sortedDates.length - 1];
    const isToday = latestDate.getTime() === todayUTC.getTime();
    const isYesterday = latestDate.getTime() === todayUTC.getTime() - 86400000;
    
    if (!isToday && !isYesterday) {
      return 0; // Streak broken if latest date is not today or yesterday
    }
    
    // Count consecutive days
    let streak = 1;
    for (let i = sortedDates.length - 1; i > 0; i--) {
      const currentDate = sortedDates[i];
      const prevDate = sortedDates[i - 1];
      
      // Check if dates are consecutive
      const diffTime = Math.abs(currentDate - prevDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  };
  
  // Function to calculate longest streak - FIXED to handle UTC dates
  const calculateLongestStreak = () => {
    if (!streakData || !streakData.datesWithGoalCompleted || streakData.datesWithGoalCompleted.length === 0) {
      return 0;
    }
    
    // Sort dates in ascending order using UTC
    const sortedDates = [...streakData.datesWithGoalCompleted]
      .map(d => {
        // Create date using UTC to avoid timezone shifts
        const date = new Date(d);
        return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
      })
      .sort((a, b) => a - b);
    
    let currentStreak = 1;
    let maxStreak = 1;
    
    for (let i = 1; i < sortedDates.length; i++) {
      const currentDate = sortedDates[i];
      const prevDate = sortedDates[i - 1];
      
      // Check if dates are consecutive
      const diffTime = Math.abs(currentDate - prevDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        currentStreak++;
        maxStreak = Math.max(maxStreak, currentStreak);
      } else {
        currentStreak = 1;
      }
    }
    
    return maxStreak;
  };
  
  // Function to count days completed this month - FIXED to handle UTC dates
  const countDaysCompletedThisMonth = () => {
    if (!streakData || !streakData.datesWithGoalCompleted) {
      return 0;
    }
    
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    return streakData.datesWithGoalCompleted.filter(date => {
      const d = new Date(date);
      // Use UTC methods to get correct month and year
      return d.getUTCMonth() === currentMonth && d.getUTCFullYear() === currentYear;
    }).length;
  };
  
  // Generate calendar data regardless of streak data
  useEffect(() => {
    // Always generate and set the calendar data, even if no streak data exists
    const calData = generateCalendarData();
    setCalendarData(calData);
  }, []);
  
  // Fetch streak data
  useEffect(() => {
    const fetchStreakData = async () => {
      if (!userId || !courseId) {
        console.warn('Missing required data for streaks:', { userId, courseId });
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        console.log('CourseId ', courseId, ' userId ', userId);
        const response = await languageLearningApi.get(`/streaks/${userId}/${courseId}`);
        
        if (response.data && response.data.data) {
          setStreakData(response.data.data);
        } else {
          console.error('Invalid response format:', response);
        }
      } catch (error) {
        console.error('Error fetching streak data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (userId && courseId) {
      fetchStreakData();
    } else {
      setLoading(false); // Set loading to false if we don't have userId or courseId
    }
  }, [userId, courseId]);

  useEffect(() => {
    if (streakData && streakData.datesWithGoalCompleted) {
      const currentStreakValue = calculateCurrentStreak();
      const longestStreakValue = calculateLongestStreak();
      const daysThisMonth = countDaysCompletedThisMonth();
      
      setCurrentStreak(currentStreakValue);
      setLongestStreak(longestStreakValue);
      setDaysCompletedThisMonth(daysThisMonth);
    }
  }, [streakData]);
  
  // Get current month name
  const getCurrentMonth = () => {
    return new Date().toLocaleString('default', { month: 'long' });
  };
  
  if (loading || !calendarData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  
  // Use the passed cardStyle prop if available, otherwise use default styles
  const defaultCardStyle = {
    borderRadius: 2,
    boxShadow: 2,
    height: '100%',
    width: '100%',
    maxWidth: '490px',
  };
  
  const finalCardStyle = { ...defaultCardStyle, ...(cardStyle || {}) };
  
  return (
    <Card sx={finalCardStyle}>
      <CardContent sx={{ p: 1.5 }}>
        {/* Header with streak icon and month */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Badge
              badgeContent={currentStreak}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.7rem',
                  height: '18px',
                  minWidth: '18px',
                  fontWeight: 'bold'
                }
              }}
            >
              <FireIcon color="error" fontSize="small" />
            </Badge>
            <Typography variant="subtitle1" fontWeight="600" sx={{ ml: 3 }}>
              Streak
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {getCurrentMonth()}
          </Typography>
        </Box>
        
        {/* Calendar by Weekday - No Week Headers */}
        <Box sx={{ mt: 1 }}>
          {calendarData.weekdayData && calendarData.weekdayData.map((weekdayRow, rowIndex) => (
            <Box 
              key={`row-${rowIndex}`} 
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                height: '38px', // Increased height to accommodate larger circles
                mb: 0.75,
                borderBottom: rowIndex < 6 ? '1px solid' : 'none',
                borderColor: theme.palette.divider,
                pb: 0.5
              }}
            >
              {/* Weekday label - single letter */}
              <Typography 
                variant="caption" 
                sx={{ 
                  width: '25px',
                  fontWeight: 'bold',
                  color: weekdayRow.weekday === 'S' 
                    ? theme.palette.text.secondary 
                    : theme.palette.text.primary,
                  fontSize: '0.7rem',
                  mr: 1 // Add margin to create space between label and circles
                }}
              >
                {weekdayRow.weekday}
              </Typography>
              
              {/* Days for this weekday */}
              <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-around' }}>
                {weekdayRow.days.map((day, dayIndex) => {
                  if (!day) {
                    // Empty cell placeholder
                    return (
                      <Box 
                        key={`empty-${rowIndex}-${dayIndex}`} 
                        sx={{ 
                          width: '30px',
                          height: '30px',
                        }}
                      />
                    );
                  }
                  
                  const isToday = day.date.getDate() === new Date().getDate() && 
                                day.date.getMonth() === new Date().getMonth() && 
                                day.date.getFullYear() === new Date().getFullYear();
                                
                  const hasGoal = hasCompletedGoal(day.dateString);
                  
                  return (
                    <Tooltip 
                      key={`day-${rowIndex}-${dayIndex}`} 
                      title={`${day.date.toLocaleDateString('default', { month: 'short', day: 'numeric' })}${hasGoal ? ' - Goal completed' : ''}`}
                    >
                      <Box 
                        sx={{ 
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%', // Perfect circle
                          bgcolor: hasGoal ? theme.palette.success.main : 'transparent',
                          border: isToday ? `2px solid ${theme.palette.primary.main}` : '1px solid',
                          borderColor: hasGoal ? 'transparent' : theme.palette.divider,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'default'
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: '0.75rem',
                            lineHeight: 1,
                            fontWeight: isToday ? 'bold' : 'medium',
                            color: hasGoal ? '#fff' : theme.palette.text.primary
                          }}
                        >
                          {day.day}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
        
        {/* Stats Row - Compact inline version */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mt: 1.5,
            pt: 1.5,
            borderTop: '1px solid',
            borderColor: theme.palette.divider
          }}
        >
          {/* Days this month */}
          <Box sx={{ textAlign: 'center', width: '33%' }}>
          <Typography variant="body1" color="primary" fontWeight="bold"  sx={{ fontSize: '1.2rem' }}>
          {daysCompletedThisMonth}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1rem' }}>
              Total Days
            </Typography>
          </Box>
          
          {/* Longest streak */}
          <Box sx={{ textAlign: 'center', width: '33%', borderLeft: '1px solid', borderRight: '1px solid', borderColor: theme.palette.divider }}>
            <Typography variant="body1" color="primary" fontWeight="bold"  sx={{ fontSize: '1.2rem' }}>
              {longestStreak}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1rem' }}>
              Longest
            </Typography>
          </Box>
          
          {/* Current streak */}
          <Box sx={{ textAlign: 'center', width: '33%' }}>
          <Typography variant="body1" color="primary" fontWeight="bold"  sx={{ fontSize: '1.2rem' }}>
          {currentStreak}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1rem' }}>
              Curr. Streak
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default UserStreakComponent;